import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Paste-to-pin Google Maps URL field for hotel / activity entries.
 * Auto-extracts lat/lng via backend and saves a verified location pin.
 *
 * Pin creation is DECOUPLED from saving the parent record: the moment the
 * admin pastes a URL and hits Verify, the shared pollen_locations row is
 * upserted. This mirrors how LocationPinBtn works for city fields.
 */
const HotelMapUrlField = ({
  entityName,
  entityType = 'hotel',      // 'hotel' | 'activity'
  city,
  initialUrl,
  initialLat,
  initialLng,
  initialVerified,
  onPinned,
  label = 'Google Maps URL',
  compact = false,
}) => {
  const [url, setUrl] = useState(initialUrl || '');
  const [busy, setBusy] = useState(false);
  const [pinned, setPinned] = useState(
    initialVerified && initialLat != null && initialLng != null
      ? { lat: initialLat, lng: initialLng }
      : null
  );

  useEffect(() => {
    setUrl(initialUrl || '');
    setPinned(
      initialVerified && initialLat != null && initialLng != null
        ? { lat: initialLat, lng: initialLng }
        : null
    );
  }, [initialUrl, initialLat, initialLng, initialVerified]);

  const verify = async () => {
    const name = (entityName || '').trim();
    if (!name) { toast.error('Enter the name first.'); return; }
    if (!url.trim()) { toast.error('Paste a Google Maps URL.'); return; }
    setBusy(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations/pin-by-name`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ name, type: entityType, url: url.trim(), city: city || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not pin');
      setPinned({ lat: data.lat, lng: data.lng });
      toast.success(`${name} pinned at ${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`);
      onPinned && onPinned({ url: url.trim(), ...data });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const hint = pinned
    ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Pinned at {pinned.lat.toFixed(4)}, {pinned.lng.toFixed(4)}</span>
    : (url.trim() ? <span className="text-amber-600 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />Tap Verify to extract coordinates.</span>
                  : <span className="text-muted-foreground">Tap Share → Copy link in Google Maps, paste here.</span>);

  return (
    <div className={compact ? '' : 'space-y-1'}>
      {label && <Label className={compact ? 'text-xs' : ''}>{label} {entityType === 'hotel' || entityType === 'activity' ? <span className="text-rose-500">*</span> : null}</Label>}
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <MapPin className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${compact ? 'w-3 h-3' : 'w-4 h-4'} text-muted-foreground`} />
          <Input
            value={url}
            onChange={e => { setUrl(e.target.value); setPinned(null); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); verify(); } }}
            placeholder="Paste Google Maps URL (or lat, lng)"
            className={`${compact ? 'h-7 text-xs pl-7' : 'h-9 pl-8'}`}
            data-testid={`hotel-map-url-input-${entityName || 'new'}`}
          />
        </div>
        <Button
          type="button"
          size={compact ? 'sm' : 'default'}
          variant={pinned ? 'secondary' : 'outline'}
          onClick={verify}
          disabled={busy || !url.trim() || !entityName?.trim()}
          className={compact ? 'h-7 text-xs px-2' : ''}
          data-testid={`hotel-map-url-verify-${entityName || 'new'}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (pinned ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : 'Verify')}
        </Button>
        {/* Open-in-Maps shortcut — always visible once a URL or pin is
            present. Always resolves to an absolute https://maps Google URL
            so the browser never interprets the value as an internal route
            (which used to happen when the user pasted bare "lat, lng"
            coordinates). */}
        {(url.trim() || pinned) && (() => {
          const raw = url.trim();
          const isAbsolute = /^https?:\/\//i.test(raw);
          const href = isAbsolute
            ? raw
            : (pinned
                ? `https://www.google.com/maps?q=${pinned.lat},${pinned.lng}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(raw)}`);
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${compact ? 'h-7 px-2 text-xs' : 'h-9 px-3 text-sm'} inline-flex items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-medium`}
              data-testid={`hotel-map-url-open-${entityName || 'new'}`}
              title="Open this pin in Google Maps"
            >
              <ExternalLink className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5 mr-1'}`} />
              {!compact && 'Open'}
            </a>
          );
        })()}
      </div>
      <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground leading-tight`}>{hint}</p>
    </div>
  );
};

export default HotelMapUrlField;
